/**
 * English UI catalogue for namespace 'checker' (W2-12a checker-runtime): the Problems panel, the problems.*
 * commands, the fix commands and their history labels, toasts, timeline marks and settings rows (DESIGN.md §4.18,
 * §5.10; the Help article "Problems" is in onboarding/help.messages.ts). Rule texts are in rules/*.messages.ts. zh-CN: src/i18n/zh-CN/checker.ts (W3-09).
 */

import { catalogOf, defineMessages } from '../../i18n/define';

const SEVERITY = '{severity, select, fatal {Fatal} error {Error} warning {Warning} tip {Tip} other {Problem}}';
const SEVERITIES = '{severity, select, fatal {Fatal} error {Errors} warning {Warnings} tip {Tips} other {Problems}}';
const CATEGORY = '{category, select, phira {Phira} gameplay {Gameplay} style {Style} other {Other}}';

export const m = defineMessages('checker', {
  // ---- panel ----
  panelTitle: 'Problems',
  listLabel: 'Problems',
  severityName: SEVERITY,
  severityFilter: `${SEVERITIES} ({count})`,
  severityFilterLabel:
    'Show {severity, select, fatal {fatal problems} error {errors} warning {warnings} tip {tips} other {problems}} ({count})',
  categoryName: CATEGORY,
  categoryFilterLabel: `Show ${CATEGORY} problems`,
  filtersLabel: 'Problem filters',
  scopeLabel: 'Show the problems of',
  scopeChart: 'Whole chart',
  scopeLine: 'Active line',
  scopeLineNone: 'Choose an active line first',
  searchLabel: 'Search problems',
  searchPlaceholder: 'Search problems…',
  checkNow: 'Check now',
  checking: 'Checking…',
  checkingRest: 'Phira checks done; checking gameplay and style…',
  checkedStats:
    'Checked {notes, plural, one {# note} other {# notes}} on {lines, plural, one {# line} other {# lines}} in {ms} ms',
  notChecked: 'Not checked yet',
  workerStopped: 'The checker stopped: problems are not updated until the chart is opened again',
  checkingTitle: 'Checking the chart…',
  checkingBody: 'Problems appear here as soon as the first check is done.',
  shownCount: '{shown} of {total, plural, one {# problem} other {# problems}}',
  rowLabel: '{severity}: {message}',
  go: 'Go',
  goLabel: 'Go to this problem',
  goNowhere: 'This problem has no place in the timeline',
  ignore: 'Ignore here',
  ignoreLabel: 'Ignore this problem in this chart (undo brings it back)',
  moreFixes: 'More fixes',
  fixesOf: 'Fixes',
  fixingNote: 'Fixed. Checking again…',
  location: '{line} · {beat}',
  locationChart: 'Whole chart',
  tipsShow: '{count, plural, one {Show # tip} other {Show # tips}}',
  tipsHide: '{count, plural, one {Hide the tip} other {Hide the # tips}}',
  tipsFoldedHint: 'Suggestions; the chart plays the same without them',
  failedRules:
    '{count, plural, one {A check could not run ({rules}): its earlier results are kept.} other {# checks could not run ({rules}): their earlier results are kept.}}',
  fatalBanner:
    '{count, plural, one {# fatal problem stops} other {# fatal problems stop}} Phira from loading this chart. Quick export is blocked until {count, plural, one {it is} other {they are}} fixed.',
  noProjectTitle: 'No chart open',
  noProjectBody: 'Open a chart to check it for Phira problems.',
  noProblemsTitle: 'No problems found',
  noProblemsBody: 'The checker runs again after every change.',
  noMatchTitle: 'No problems match the filters',
  noMatchBody: 'Other problems are hidden by the severity, category, scope or search filters.',
  showAll: 'Show all',
  ignoredTitle: 'Ignored in this chart ({count})',
  ignoredItem: '{rule} · {title}',
  ignoredUnknown: '{rule}',
  stopIgnoring: 'Stop ignoring',
  stopIgnoringAll: 'Stop ignoring all',

  // ---- commands ----
  cmdShow: 'Show problems',
  cmdNext: 'Next problem',
  cmdPrev: 'Previous problem',
  cmdQuickFix: 'Quick fix (apply the first fix)',
  cmdIgnore: 'Ignore the current problem here',
  cmdUnignoreAll: 'Stop ignoring all problems of this chart',
  cmdCheckNow: 'Check the chart now',
  reasonNoProject: 'Open a chart first',
  reasonNoProblems: 'There are no problems',
  reasonNoCurrent: 'Go to a problem first (Next problem)',
  reasonNoIgnored: 'No problem is ignored in this chart',

  // ---- toasts ----
  toastNoProblems: 'No problems to go to',
  toastNoFix: 'This problem has no fix',
  toastNoTarget: 'Go to a problem first: Next problem selects one',
  toastIgnored: 'Ignored in this chart',
  toastUnignored: '{count, plural, one {The problem is} other {# problems are}} checked again',
  fixNothingChanged: 'Nothing to change: the chart already looks right',
  fixGone: 'This problem is gone: the chart changed since it was found',
  fixInvalid: 'This fix no longer fits the chart',
  fixNoProject: 'Open a chart first',
  fixMediaFailed: 'The file could not be converted',
  fixMediaRead: 'The file could not be read from the project storage',
  fixMediaNoDecoder: 'This browser cannot decode audio, so the file cannot be converted here',
  fixMediaDecodeAudio: 'The browser could not decode this sound file',
  fixMediaNoAudio: 'The file contains no audio',
  fixMediaImage: 'The image could not be decoded or converted',
  fixMediaGif: 'The GIF file is damaged and cannot be re-timed',
  fixMediaStore: 'The converted file could not be saved: the project storage is full or unavailable',
  fixUnavailable: 'This fix needs the project storage, which is not available',

  // ---- history labels ----
  txnFix: 'Fix a problem',
  txnIgnore: 'Ignore problem {rule}',
  txnUnignore: '{count, plural, one {Stop ignoring a problem} other {Stop ignoring # problems}}',

  // ---- fix commands ----
  fixSetLineOpacityFull: 'Fix: show the line (opacity 100 %)',
  fixHoldLeadIn: 'Fix: keep the first value before the first keyframe',
  fixSetEase: 'Fix: change the curve',
  fixSetKeyValue: 'Fix: change a keyframe value',
  fixClampKeyValues: 'Fix: clamp keyframe values',
  fixRemoveGifProgress: 'Fix: remove the GIF progress',
  fixClampPreviewRange: 'Fix: move the song preview into the song',
  fixSnapNotesX: 'Fix: snap notes to the X grid',
  fixDeleteNotes: 'Fix: delete notes',
  fixMoveNotesLater: 'Fix: move notes one snap later',
  fixAlignNoteTimes: 'Fix: put notes at the same time',
  fixSetIncline: 'Fix: keep a constant note tilt',
  fixResetRpeLineFields: 'Fix: reset fields only RPE reads',
  fixAddWhiteColorKey: 'Fix: add a white colour keyframe',
  fixHoldVarLeadIn: 'Fix: hold an effect variable from the effect start',
  fixRenameAsset: 'Fix: rename a file',
  fixRenameHitsoundAsset: 'Fix: rename a hit sound file',
  fixDeleteEffect: 'Fix: delete an effect',
  fixSetEffectVar: 'Fix: set an effect variable',
  fixWriteIntegerRpeVersion: 'Fix: write RPEVersion as a whole number',
  fixExtendHolds: 'Fix: make holds one snap long',
  fixConvertNotes: 'Fix: convert notes',
  fixSetControl: 'Fix: change a note control curve',
  fixRemoveAssetReferences: 'Fix: remove a file and its uses',
  fixConvertAudioToWav: 'Fix: convert audio to WAV',
  fixConvertImageToPng: 'Fix: convert an image to PNG',
  fixReencodeGif: 'Fix: re-time the frames of a GIF',
  fixDownscaleImage: 'Fix: downscale an image',
  fixMoveNotesToNewLine: 'Fix: move notes off a UI line',
  fixRemoveIgnoredLineData: 'Fix: remove line data Phira ignores',
  fixConfirmTiming: 'Fix: mark the timing as confirmed',
  fixRemoveAssets: 'Fix: remove unused files',
  fixDeleteLines: 'Fix: delete empty lines',
  fixRemoveRedundantKeys: 'Fix: remove keyframes that do not change the motion',
  fixAddTag: 'Fix: add a tag',

  // ---- timeline marks ----
  tickMore: '{message} (+{count} more)',

  // ---- settings rows ----
  setHideTicks: 'Hide problem marks in the timeline',
  setHideTicksDesc:
    'Problems stay in the Problems tab; the ticks beside the time ruler and in the overview strip go away.',
  setHideTipTicks: 'Leave tips out of the timeline marks',
  setHideTipTicksDesc:
    'Only fatal problems, errors and warnings get ticks beside the time ruler and in the overview strip.',
  setExplainAll: 'Explain every problem in the list',
  setExplainAllDesc:
    'Show why each problem matters in Phira under every row. Otherwise only the problem you are on shows it; the others keep it in their tooltip.',

  // ---- files missing from this browser's storage (storedFiles.ts; PH-01 / PH-02) ----
  storedMissing: '{song, select, yes {The song} other {The file}} "{path}" is missing from this browser\'s storage.',
  storedMissingWhy:
    'The browser dropped its stored copy, so the exported package would lack it and Phira could not load the chart. Choose the same file again to restore it.',
  fixChooseAgain: 'Choose the file again',
});

export default catalogOf(m);
